import type { AiStrategy, GameState } from '../game/types';

/**
 * 联机房间 API 客户端。
 * 开发环境通过 Vite 代理访问 /api；其他环境可用
 * VITE_ROOM_SERVER_URL 指向独立部署的 Node 服务端。
 */

const BASE_URL = (import.meta.env.VITE_ROOM_SERVER_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export type RoomPhase = 'lobby' | 'playing' | 'processing';

export interface RoomSettings {
  aiCount: number;
  aiDifficulty: AiStrategy;
}

export interface RoomPlayerSummary {
  id: string;
  name: string;
  ready: boolean;
  /** 大厅阶段对其他玩家隐藏（盲选），游戏开始后公开 */
  chosenCity: string | null;
  week: number;
  cash: number;
  companyName: string;
  gameOver: boolean;
  updatedAt: number;
}

export interface RoomSnapshot {
  id: string;
  createdAt: number;
  phase: RoomPhase;
  week: number;
  ownerId: string;
  settings: RoomSettings;
  players: RoomPlayerSummary[];
  me: { id: string; name: string; ready: boolean; chosenCity: string | null; state: GameState | null } | null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
  });
  const payload = (await response.json().catch(() => null)) as ({ error?: string } & T) | null;
  if (!response.ok) {
    throw new Error(payload?.error ?? `服务器错误（${response.status}）`);
  }
  if (!payload) throw new Error('服务器返回了无法解析的数据');
  return payload;
}

export async function createRoom(name: string): Promise<{ roomId: string; playerId: string; room: RoomSnapshot }> {
  return request('/api/rooms', { method: 'POST', body: JSON.stringify({ name }) });
}

export async function joinRoom(roomId: string, name: string): Promise<{ roomId: string; playerId: string; room: RoomSnapshot }> {
  return request(`/api/rooms/${encodeURIComponent(roomId.toUpperCase())}/join`, { method: 'POST', body: JSON.stringify({ name }) });
}

export async function fetchRoom(roomId: string, playerId?: string): Promise<{ room: RoomSnapshot }> {
  const query = playerId ? `?playerId=${encodeURIComponent(playerId)}` : '';
  return request(`/api/rooms/${encodeURIComponent(roomId)}${query}`);
}

export async function updateRoomSettings(roomId: string, playerId: string, settings: RoomSettings): Promise<{ room: RoomSnapshot }> {
  return request(`/api/rooms/${encodeURIComponent(roomId)}/settings`, { method: 'POST', body: JSON.stringify({ playerId, ...settings }) });
}

export async function chooseCity(roomId: string, playerId: string, city: string): Promise<{ room: RoomSnapshot }> {
  return request(`/api/rooms/${encodeURIComponent(roomId)}/choose`, { method: 'POST', body: JSON.stringify({ playerId, city }) });
}

export async function setPlayerReady(roomId: string, playerId: string, ready: boolean): Promise<{ room: RoomSnapshot }> {
  return request(`/api/rooms/${encodeURIComponent(roomId)}/ready`, { method: 'POST', body: JSON.stringify({ playerId, ready }) });
}

export async function startRoomGame(roomId: string, playerId: string): Promise<{ room: RoomSnapshot }> {
  return request(`/api/rooms/${encodeURIComponent(roomId)}/start`, { method: 'POST', body: JSON.stringify({ playerId }) });
}

export async function submitState(roomId: string, playerId: string, state: GameState): Promise<{ room: RoomSnapshot }> {
  return request(`/api/rooms/${encodeURIComponent(roomId)}/submit`, { method: 'POST', body: JSON.stringify({ playerId, state }) });
}

export async function leaveRoom(roomId: string, playerId: string): Promise<{ ok: true }> {
  return request(`/api/rooms/${encodeURIComponent(roomId)}/leave`, { method: 'POST', body: JSON.stringify({ playerId }) });
}
